import { useState, useEffect } from "react";
import { useApi } from "../../hooks/useApi";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Dayjs } from "dayjs";
import * as Sentry from "@sentry/react";
import { PresetKey, presets } from "../common/dataFilterPresets";
import { useBrandId } from "../../hooks/useBrandId";

interface VerifiNoticesCsvDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: number | null;
  caseType?: string;
  title?: string;
  showVerifiPortalFormat?: boolean;
}

export default function VerifiNoticesCsvDialog({ open, onClose, companyId, caseType = 'RDR_NOTICE', title = 'RDR Notices', showVerifiPortalFormat = true }: VerifiNoticesCsvDialogProps) {
  const { brandId } = useBrandId();
  const { download, loading, error: apiError } = useApi();

  const [preset, setPreset] = useState<PresetKey>("7D");
  const [startDate, setStartDate] = useState<Dayjs | null>(presets["7D"].start());
  const [endDate, setEndDate] = useState<Dayjs | null>(presets["7D"].end());
  const [downloading, setDownloading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPreset("7D");
      setStartDate(presets["7D"].start());
      setEndDate(presets["7D"].end());
      setPageError(null);
      setDownloading(false);
    }
  }, [open]);

  const handlePresetChange = (e: SelectChangeEvent<PresetKey>) => {
    const key = e.target.value as PresetKey;
    setPreset(key);
    setStartDate(presets[key].start());
    setEndDate(presets[key].end());
  };

  const handleStartDateChange = (date: Dayjs | null) => {
    setStartDate(date);
    setPreset("custom");
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    setEndDate(date);
    setPreset("custom");
  };

  const handleDownload = async (format: 'default' | 'verifi_portal') => {
    setPageError(null);
    setDownloading(true);

    if (!startDate?.isValid() || !endDate?.isValid()) {
      setPageError('Please select valid start and end dates');
      setDownloading(false);
      return;
    }

    try {
      const params = new URLSearchParams({
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        caseType,
        format,
      });

      if (companyId) {
        params.set('companyId', companyId.toString());
      }
      if (brandId) {
        params.set('brandId', brandId.toString());
      }

      await download(`/api/alerts/verifi-notices/csv?${params.toString()}`);
      onClose();
    } catch (err) {
      console.error('Failed to download CSV:', err);
      setPageError(err instanceof Error ? err.message : 'Unknown error');
      Sentry.captureException(err);
    } finally {
      setDownloading(false);
    }
  };

  const handleClose = () => {
    setPageError(null);
    setDownloading(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick') {
          handleClose();
        }
      }}
      aria-labelledby="verifi-csv-dialog-title"
      maxWidth={false}
    >
      <DialogTitle id="verifi-csv-dialog-title">
        Export {title} CSV
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <FormControl size="small" sx={{ width: 120 }}>
                <InputLabel id="verifi-csv-preset-label">Range</InputLabel>
                <Select
                  labelId="verifi-csv-preset-label"
                  value={preset}
                  label="Range"
                  onChange={handlePresetChange}
                >
                  {Object.keys(presets).map((key) => (
                    <MenuItem key={key} value={key}>
                      {presets[key as PresetKey].label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { width: 150 },
                  },
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { width: 150 },
                  },
                }}
              />
            </Box>

            {(apiError || pageError) && (
              <Box className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                Error: {apiError || pageError}
              </Box>
            )}
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={() => handleDownload('default')}
          variant="contained"
          disabled={loading || downloading || !startDate?.isValid() || !endDate?.isValid()}
          sx={{
            backgroundColor: '#465fff',
            '&:hover': {
              backgroundColor: '#3641f5',
            },
          }}
        >
          {downloading ? 'Downloading...' : 'Default CSV'}
        </Button>
        {showVerifiPortalFormat && (
          <Button
            onClick={() => handleDownload('verifi_portal')}
            variant="contained"
            disabled={loading || downloading || !startDate?.isValid() || !endDate?.isValid()}
            sx={{
              backgroundColor: '#465fff',
              '&:hover': {
                backgroundColor: '#3641f5',
              },
            }}
          >
            {downloading ? 'Downloading...' : 'Verifi Portal CSV'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
